import * as React from "react";
import { cn } from "../lib";

export type ButtonVariant =
  | "primary"
  | "brand"
  | "outline"
  | "ghost"
  | "danger"
  | "destructive";

export type ButtonSize = "sm" | "md" | "lg" | "icon";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-button-dark text-white shadow-xs hover:bg-button-darkHover active:bg-button-darkActive active:scale-[0.99] disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed disabled:opacity-100 disabled:shadow-none",
  brand:
    "bg-brand text-white shadow-xs hover:bg-brand-dark active:bg-brand-active active:scale-[0.99] disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed disabled:opacity-100 disabled:shadow-none",
  outline:
    "border border-neutral-300 bg-white text-neutral-700 shadow-xs hover:bg-neutral-50 active:bg-neutral-100 active:scale-[0.99] disabled:border-neutral-200 disabled:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none",
  ghost:
    "bg-transparent text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 active:bg-neutral-200 active:scale-[0.99] disabled:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50",
  danger:
    "bg-danger-600 text-white shadow-xs hover:bg-danger-700 active:bg-danger-800 active:scale-[0.99] disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed disabled:opacity-100 disabled:shadow-none",
  destructive:
    "bg-danger-600 text-white shadow-xs hover:bg-danger-700 active:bg-danger-800 active:scale-[0.99] disabled:bg-button-disabled disabled:text-white disabled:cursor-not-allowed disabled:opacity-100 disabled:shadow-none",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-xs",
  md: "h-10 px-5 text-sm",
  lg: "h-12 px-6 text-base",
  icon: "h-10 w-10 p-0",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "primary", size = "md", type = "button", ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-[8px] font-medium transition-all duration-150 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      />
    );
  },
);

Button.displayName = "Button";

export { Button };
